/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { observable, action } from 'mobx';

class AppsPanelSkeleton {
    @observable skeletonLayoutID;

    @observable sectionGroupList = [];

    @action
    setSkeletonLayoutID(id) {
        if (id && typeof id === 'string') {
            this.skeletonLayoutID = id;
        }
    }

    @action
    addSectionGroup(categorySkeleton) {
        this.sectionGroupList.push(categorySkeleton);
    }

    @action
    clearSectionGroup() {
        this.sectionGroupList.clear();
    }

    setSelectedSubSection(subSection) {
        this.selectedSubSection = subSection;
    }

    setSelectedSectionFilter(section) {
        this.selectedSectionFilter = section;
    }

    getActionItem(navigationCategoryId, sectionId, actionId) {
        let actionItem = null;
        const navCategory = this.getSectionGroup(navigationCategoryId);
        if (navCategory.length > 0) {
            const section = navCategory[0].getAppSection(sectionId);
            if (section.length > 0) {
                const actionItems = section[0].getActionItem(actionId);
                if (actionItems.length > 0) {
                    [actionItem] = actionItems;
                }
            }
        }
        return actionItem;
    }

    getSectionGroup(id) {
        return this.sectionGroupList.filter(
            sectionGroup => (
                sectionGroup.getId() === id
            )
        );
    }

    addActionItem(navigationCategoryId, sectionId, actionId) {
        const navCategory = this.getSectionGroup(navigationCategoryId);
        if (navCategory.length > 0) {
            const section = navCategory[0].getAppSection(sectionId);
            if (section.length > 0) {
                section[0].displayActionItem(actionId);
            }
        }
    }

    removeActionItem(navigationCategoryId, sectionId, actionId) {
        const navCategory = this.getSectionGroup(navigationCategoryId);
        if (navCategory.length > 0) {
            const section = navCategory[0].getAppSection(sectionId);
            if (section.length > 0) {
                section[0].hideActionItem(actionId);
            }
        }
    }

    enableActionItem(navigationCategoryId, sectionId, actionId) {
        const navCategory = this.getSectionGroup(navigationCategoryId);
        if (navCategory.length > 0) {
            const section = navCategory[0].getAppSection(sectionId);
            if (section.length > 0) {
                section[0].enableActionItem(actionId);
            }
        }
    }

    disableActionItem(navigationCategoryId, sectionId, actionId) {
        const navCategory = this.getSectionGroup(navigationCategoryId);
        if (navCategory.length > 0) {
            const section = navCategory[0].getAppSection(sectionId);
            if (section.length > 0) {
                section[0].disableActionItem(actionId);
            }
        }
    }

    getAppSection(sectionId) {
        let filteredSections = [];
        // eslint-disable-next-line consistent-return
        this.sectionGroupList.forEach((navCategory) => {
            filteredSections = navCategory.getAppSection(sectionId);
            if (filteredSections.length) {
                return filteredSections;
            }
        });
        return filteredSections;
    }

    removeAppSection(sectionId) {
        this.sectionGroupList.forEach((navCategory) => {
            navCategory.removeAppSection(sectionId);
        });
    }

    disableAppSection(sectionId) {
        const filteredSections = this.getAppSection(sectionId);
        if (filteredSections.length) {
            filteredSections[0].setDisabled(true);
        }
    }

    enableAppSection(sectionId) {
        const filteredSections = this.getAppSection(sectionId);
        if (filteredSections.length) {
            filteredSections[0].setDisabled(false);
        }
    }
}

export default new AppsPanelSkeleton();
